import _ from "lodash";
import { normalizeText } from "normalize-text";
const charMap: CharMap = { map: {}, results: null };
let addedKeys: string[] = [];
const fetchMap: { [key: string]: Promise<SearchResultComplete> } = {};
let emptyInputResult: SearchResultComplete = null;

export const textSearch = async (
    inputValue: string
): Promise<SearchResult[]> => {
    if (!inputValue) {
        return await handleEmptyInput();
    }
    return (await iterateCharacters(normalize(inputValue))).slice(0, 10);
};

const handleEmptyInput = async () => {
    let data: SearchResultComplete = emptyInputResult;
    if (!data) {
        data = await fetchOrJoinToExistingFetch("_");
        emptyInputResult = data;
    }
    return data.records;
};

const iterateCharacters = async (
    inputValue: string
): Promise<SearchResult[]> => {
    let currentMap: CharMap = charMap;
    let needLocalSearch = false;
    for (var i = 0; i < inputValue.length; i++) {
        const searchValue = inputValue.substring(0, i + 1);
        const currentChar = inputValue.charAt(i);
        if (
            i != 0 &&
            !_.includes(currentMap.results?.next || [], currentChar)
        ) {
            needLocalSearch = true;
            break;
        }
        let newMap = currentMap.map[searchValue];
        if (!newMap) {
            const data = await fetchOrJoinToExistingFetch(
                searchValue,
                currentMap
            );
            newMap = currentMap.map[searchValue];
        }
        currentMap = newMap;
    }
    let res = currentMap.results?.records || [];
    if (needLocalSearch) {
        res = localSearch(res, inputValue);
    }
    checkMemorySizeTooBig(inputValue);
    return res;
};

const localSearch = (records: SearchResult[], inputValue): SearchResult[] => {
    return records.filter((r) => {
        let parts = r.name.split(/,| |`|'/g).map((n) => normalize(n));
        const stringsToCheck = [normalize(r.name)];
        if (parts.length > 1) {
            const startingLetters = parts.reduce(
                (prev, cur) => prev + cur[0],
                ""
            );
            stringsToCheck.push(startingLetters);
            const startingLettersImportant = parts.reduce((prev, cur) => {
                const startingLetter = cur[0];
                return prev + (cur.length < 3 ? "" : startingLetter);
            }, "");
            if (startingLetters != startingLettersImportant) {
                stringsToCheck.push(startingLettersImportant);
            }
        }
        for (let str of stringsToCheck) {
            if (str.includes(inputValue)) return true;
        }
        return false;
    });
};

const fetchOrJoinToExistingFetch = async (
    searchValue: string,
    currentMap?: CharMap
): Promise<SearchResultComplete> => {
    let existingPromise = fetchMap[searchValue];
    if (existingPromise) {
        return await existingPromise;
    }
    const promise: Promise<SearchResultComplete> = fetch(
        `/search-api/${searchValue}.json`
    )
        .then(async (resp) => await resp.json())
        .then((data: SearchResultComplete) => {
            if (currentMap) {
                currentMap.map[searchValue] = {
                    results: data,
                    map: {}
                };
                addedKeys.push(searchValue);
            }
            return data;
        });
    fetchMap[searchValue] = promise;
    const data: SearchResultComplete = await promise;
    delete fetchMap[searchValue];

    return data;
};

const normalize = (str: string): string => {
    let nfd;
    if (String.prototype.normalize) {
        nfd = str
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[ł]/g, "l");
    } else {
        nfd = normalizeText(str);
    }
    return nfd
        .toLowerCase()
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[ł]/g, "l")
        .replace(/[^a-z0-9]/g, "");
};

const checkMemorySizeTooBig = (inputValue: string) => {
    setTimeout(() => {
        if (addedKeys.length < 100) return;
        // leaving current search in memory as well as all ongoing fetches
        let ongoingLettersFetch = [inputValue[0]];
        for (let key in fetchMap) {
            ongoingLettersFetch.push(key[0]);
        }
        ongoingLettersFetch = _.uniq(ongoingLettersFetch);
        for (let key in charMap.map) {
            if (ongoingLettersFetch.includes(key)) continue;
            delete charMap.map[key];
        }
        addedKeys = addedKeys.filter((k) => ongoingLettersFetch.includes(k[0]));
    });
};

type CharMap = {
    map: { [key: string]: CharMap };
    results: SearchResultComplete;
};

export type SearchResult = {
    res?: string;
    reg?: string;
    cou?: string;
    con?: string;
    isMulti?: boolean;
    name: string;
    cName?: string;
    pathSlug?: string;
    img?: string;
    t: string;
};

type SearchResultComplete = {
    next: string[];
    records: SearchResult[];
};
